import { Link } from "@tanstack/react-router";
import { BUSINESS, SERVICES, CITIES, TRADE_HUBS, SOCIAL_PROFILES } from "@/lib/site-data";
import { CallNowButton } from "@/components/CallNowButton";

const SOCIAL_LABELS: Record<keyof typeof SOCIAL_PROFILES, string> = {
  youtube: "YouTube",
  reddit: "Reddit",
  linkedin: "LinkedIn",
  x: "X",
  quora: "Quora",
  podcast: "Podcast",
};

export function SiteFooter() {
  const socials = (Object.entries(SOCIAL_PROFILES) as Array<
    [keyof typeof SOCIAL_PROFILES, string]
  >).filter(([, url]) => url.length > 0);
  return (
    <footer className="mt-24 border-t border-border bg-background text-foreground">
      <div className="mx-auto grid max-w-6xl gap-10 px-6 py-16 md:grid-cols-5">
        <div>
          <div className="font-display text-2xl tracking-tight">{BUSINESS.name}</div>
          <p className="mt-2 text-sm text-muted-foreground">{BUSINESS.tagline}</p>
          <p className="mt-4 text-sm">
            <CallNowButton variant="link" label={BUSINESS.phone} />
          </p>
          <p className="text-sm text-muted-foreground">{BUSINESS.hours}</p>
          {socials.length > 0 && (
            <ul className="mt-4 flex flex-wrap gap-2 text-xs">
              {socials.map(([key, url]) => (
                <li key={key}>
                  <a
                    href={url}
                    rel="noopener noreferrer me"
                    target="_blank"
                    className="inline-flex rounded-full border border-border px-3 py-1 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  >
                    {SOCIAL_LABELS[key]}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Services</p>
          <ul className="mt-4 space-y-2 text-sm">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to="/services/$service" params={{ service: s.slug }} className="text-muted-foreground hover:text-foreground">
                  {s.short}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Trade Hubs</p>
          <ul className="mt-4 space-y-2 text-sm">
            {TRADE_HUBS.map((h) => (
              <li key={h.slug}>
                <Link
                  to="/hubs/$trade"
                  params={{ trade: h.slug }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {h.trade}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/case-studies" className="text-muted-foreground hover:text-foreground">
                Case studies
              </Link>
            </li>
            <li>
              <Link to="/tools/roi-calculator" className="text-muted-foreground hover:text-foreground">
                ROI calculator
              </Link>
            </li>
            <li>
              <Link to="/data/trades-cpc" className="text-muted-foreground hover:text-foreground">
                Trades CPC data
              </Link>
            </li>
            <li>
              <Link to="/press" className="text-muted-foreground hover:text-foreground">
                Press & podcasts
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Service Areas</p>
          <ul className="mt-4 space-y-2 text-sm">
            {CITIES.map((c) => (
              <li key={c.slug}>
                <Link to="/service-areas/$city" params={{ city: c.slug }} className="text-muted-foreground hover:text-foreground">
                  {c.name}, {c.region}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Company</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/about" className="text-muted-foreground hover:text-foreground">About</Link></li>
            <li><Link to="/contact" className="text-muted-foreground hover:text-foreground">Contact</Link></li>
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">
            {BUSINESS.address.street}<br />
            {BUSINESS.address.locality}, {BUSINESS.address.region} {BUSINESS.address.postalCode}<br />
            {BUSINESS.license}
          </p>
        </div>
      </div>
      <div className="border-t border-border px-4 py-5 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.
      </div>
    </footer>
  );
}